// Synchronous import — extracted to <link> in <head> before React loads
import './styles/critical.css';
import { prepareBrowserSessionLogout } from './utils/Login/browserSessionLogout';

// Pre-React storage hygiene. Runs before bootstrap so any module that reads
// merchantDetails on import (toolkit helpers, remotes via Module Federation)
// sees a clean value.
//
// If the localStorage entry is corrupted (non-JSON, primitive, array, or
// null), remove it so downstream readers don't crash on JSON.parse.
(() => {
  try {
    const raw = localStorage.getItem('merchantDetails');
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      localStorage.removeItem('merchantDetails');
    }
  } catch {
    try {
      localStorage.removeItem('merchantDetails');
    } catch {
      /* storage inaccessible */
    }
  }
})();

// Scrub sensitive URL params synchronously, BEFORE bootstrap (and any third
// party scripts loaded by it) can read them. Anything left on the URL leaks
// via Referer headers, browser history, server access logs, and analytics.
// We snapshot the values onto a non-enumerable window field so the login
// flow can still prefill its form on first render.
(() => {
  try {
    const sensitiveKeys = [
      'mfa',
      'password',
      'resetCode',
      'username',
      'userName',
      'fullname',
    ];
    const url = new URL(window.location.href);
    const snapshot: Record<string, string> = {};
    let dirty = false;
    sensitiveKeys.forEach(k => {
      const v = url.searchParams.get(k);
      if (v !== null) {
        snapshot[k] = v;
        url.searchParams.delete(k);
        dirty = true;
      }
    });
    Object.defineProperty(window, '__authParams', {
      value: snapshot,
      writable: false,
      configurable: false,
      enumerable: false,
    });
    if (dirty) {
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    }
  } catch {
    // If URL parsing or history APIs fail (very old browsers, sandboxed
    // contexts), fall through silently — the login flow will fall back to
    // reading from window.location like before.
  }
})();

// Bounded bootstrap retry. Without a counter, a persistent dynamic-import
// failure (CDN cache miss during a deploy rollover, network outage) would
// reload-loop the tab at 1Hz, hammering CloudFront. Cap retries and clear
// the counter on success.
const BOOTSTRAP_RETRY_KEY = 'bootstrap-retry-count';
const BOOTSTRAP_MAX_RETRIES = 2;

const loadBootstrap = () =>
  import('./bootstrap').then(() => {
    try {
      sessionStorage.removeItem(BOOTSTRAP_RETRY_KEY);
    } catch {
      /* storage inaccessible */
    }
  });

prepareBrowserSessionLogout()
  .then(loadBootstrap, loadBootstrap)
  .catch(error => {
    console.error('Failed to load bootstrap', error);
    let attempt = 0;
    try {
      attempt = Number(sessionStorage.getItem(BOOTSTRAP_RETRY_KEY) ?? '0') + 1;
    } catch {
      // sessionStorage inaccessible (private mode, disabled storage) \u2014
      // fall through to the no-retry branch so we don't loop.
      attempt = BOOTSTRAP_MAX_RETRIES + 1;
    }

    if (attempt > BOOTSTRAP_MAX_RETRIES) {
      try {
        sessionStorage.removeItem(BOOTSTRAP_RETRY_KEY);
      } catch {
        /* ignore */
      }
      // Surface a minimal, static fallback so the user sees something
      // actionable instead of a blank page or a reload loop.
      document.body.innerHTML =
        '<p style="font-family:sans-serif;padding:2rem">Unable to load. Please refresh in a moment.</p>';
      return;
    }

    try {
      sessionStorage.setItem(BOOTSTRAP_RETRY_KEY, String(attempt));
    } catch {
      /* ignore */
    }
    // Linear backoff (1s, 2s) keeps recovery fast for transient CDN blips
    // while still giving the edge time to resolve a deploy rollover.
    setTimeout(() => window.location.reload(), 1000 * attempt);
  });

// The below code was for XSS protection on the initial load. Need to add Custom Error Response in Cloudfront to allow loading the app even if there are dangerous query params. Will revisit in the future.
// const DANGEROUS_PARAM_PATTERN = /<|>|javascript:|on\w+\s*=|script/i;

// const hasCognitoSession = () => {
//   try {
//     for (let i = 0; i < localStorage.length; i++) {
//       const key = localStorage.key(i);
//       if (
//         key?.startsWith('CognitoIdentityServiceProvider.') &&
//         key.endsWith('.accessToken')
//       ) {
//         return true;
//       }
//     }
//   } catch {
//     // localStorage unavailable (private mode, etc.) — treat as logged out
//   }
//   return false;
// };

// const hasDangerousParams = () => {
//   const search = window.location.search;
//   if (!search) return false;
//   const decoded = (() => {
//     try {
//       return decodeURIComponent(search);
//     } catch {
//       return search;
//     }
//   })();
//   return (
//     DANGEROUS_PARAM_PATTERN.test(search) || DANGEROUS_PARAM_PATTERN.test(decoded)
//   );
// };

// if (hasDangerousParams()) {
//   window.location.replace(hasCognitoSession() ? '/dashboard' : '/login');
// } else {
//   import('./bootstrap').catch(error => {
//     console.error('Failed to load bootstrap, retrying...', error);
//     // Retry once on failure (common in Module Federation with network issues)
//     setTimeout(() => {
//       window.location.reload();
//     }, 1000);
//   });
// }
